// Tab „Sicherheit“ (design_handoff_profil §4): Passwort-Formular (handlePasswordChange unverändert), Login-Schutz
// (statisch wie bisher) und Login-Historie als Knopf (handleOpenHistory).
import { useState, type FormEvent, type ReactNode } from 'react';
import { ArrowUpRight, CircleNotch, ClockCounterClockwise, Eye, EyeSlash, Key, LockSimple, ShieldCheck } from '@phosphor-icons/react';
import { ProfilWidget, verlauf } from './Bausteine';

function PasswortFeld({ label, wert, onAendern, platzhalter, sichtbar, auge }: {
  label: string; wert: string; onAendern: (v: string) => void; platzhalter: string; sichtbar: boolean; auge?: ReactNode;
}) {
  const [fokus, setFokus] = useState(false);
  return (
    <label className="flex flex-col" style={{ gap: 8 }}>
      <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--ink2)' }}>{label}</span>
      <span className="flex items-center"
        style={{ gap: 10, height: 50, padding: auge ? '0 6px 0 14px' : '0 14px', borderRadius: 16, background: 'var(--fill)', transition: 'box-shadow .2s',
          boxShadow: fokus ? 'inset 0 0 0 1.5px var(--acc)' : 'inset 0 0 0 1px var(--line)' }}>
        <LockSimple size={18} weight="bold" color={fokus ? 'var(--acc)' : 'var(--ink3)'} style={{ transition: 'color .2s' }} />
        <input type={sichtbar ? 'text' : 'password'} value={wert} onChange={(e) => onAendern(e.target.value)} placeholder={platzhalter}
          onFocus={() => setFokus(true)} onBlur={() => setFokus(false)} autoComplete="new-password"
          className="min-w-0 flex-1" style={{ border: 'none', outline: 'none', background: 'transparent', fontFamily: 'inherit', fontSize: 15, fontWeight: 600, color: 'var(--ink)' }} />
        {auge}
      </span>
    </label>
  );
}

export function SicherheitTab({ breite, mobil, newPassword, confirmPassword, showPasswords, loading, onNeu, onBestaetigt, onSichtbar, onSubmit, onHistorie }: {
  breite: number; mobil: boolean;
  newPassword: string; confirmPassword: string; showPasswords: boolean; loading: boolean;
  onNeu: (v: string) => void; onBestaetigt: (v: string) => void; onSichtbar: () => void;
  onSubmit: (e: FormEvent) => void; onHistorie: () => void;
}) {
  const breit = breite >= 1000;
  const nebeneinander = !breit && breite >= 640;
  const gesperrt = loading || !newPassword;
  const karte = { flex: 1, borderRadius: 28, padding: '20px 22px', gap: 18 } as const;
  return (
    <div className="grid items-stretch" style={{ gridTemplateColumns: 'repeat(12, minmax(0, 1fr))', gap: 14 }}>
      <ProfilWidget als="form" onSubmit={onSubmit} titel="Sicherheit & Privatsphäre" untertitel="Passwort erneuern und Schutzoptionen verwalten"
        spalte={breit ? 'span 7' : '1 / -1'}
        icon={<span className="flex shrink-0 items-center justify-center" style={{ width: 38, height: 38, borderRadius: 12, background: 'var(--acc-fill)', color: 'var(--acc-ink)', boxShadow: 'var(--acc-sh)' }}><Key size={19} weight="fill" /></span>}>
        <PasswortFeld label="Neues Passwort" wert={newPassword} onAendern={onNeu} platzhalter="Min. 6 Zeichen" sichtbar={showPasswords} />
        <PasswortFeld label="Passwort bestätigen" wert={confirmPassword} onAendern={onBestaetigt} platzhalter="Passwort wiederholen" sichtbar={showPasswords}
          auge={
            <button type="button" onClick={onSichtbar} aria-label={showPasswords ? 'Passwörter ausblenden' : 'Passwörter anzeigen'} aria-pressed={showPasswords}
              data-tip={showPasswords ? 'Verbergen – Passwörter ausblenden' : 'Anzeigen – Passwörter im Klartext zeigen'}
              className="flex shrink-0 items-center justify-center hover:bg-[var(--fill2)]"
              style={{ width: mobil ? 44 : 38, height: mobil ? 44 : 38, border: 'none', borderRadius: '50%', cursor: 'pointer', background: 'transparent', color: 'var(--ink3)' }}>
              {showPasswords ? <EyeSlash size={18} weight="bold" /> : <Eye size={18} weight="bold" />}
            </button>
          } />
        <button type="submit" disabled={gesperrt} data-tip="Passwort aktualisieren – Neues Passwort speichern"
          className="flex items-center justify-center"
          style={{ height: 50, marginTop: 4, gap: 9, border: 'none', borderRadius: 25, fontFamily: 'inherit', fontSize: 15, fontWeight: 800,
            cursor: gesperrt ? 'not-allowed' : 'pointer', opacity: gesperrt ? 0.5 : 1, transition: 'opacity .2s',
            background: 'var(--acc-fill)', color: 'var(--acc-ink)', boxShadow: 'var(--acc-sh)' }}>
          {loading ? <CircleNotch size={17} weight="bold" className="animate-spin" /> : <Key size={17} weight="bold" />}
          Passwort aktualisieren
        </button>
      </ProfilWidget>

      <div className="flex" style={{ gridColumn: breit ? 'span 5' : '1 / -1', flexDirection: nebeneinander ? 'row' : 'column', gap: 14 }}>
        <section aria-label="Login-Schutz" data-tip="Login-Schutz – Angezeigter Status, keine Einstellung" className="immo-glas flex flex-col justify-between" style={karte}>
          <div className="flex items-start justify-between" style={{ gap: 10 }}>
            <span className="flex items-center justify-center" style={{ width: 46, height: 46, borderRadius: 14, background: verlauf('--ok'), color: '#fff',
              boxShadow: 'inset 0 1px 1px rgba(255,255,255,0.35), 0 12px 24px -12px var(--ok)' }}>
              <ShieldCheck size={23} weight="fill" />
            </span>
            <span className="inline-flex items-center" style={{ height: 24, padding: '0 10px', gap: 6, borderRadius: 12, fontSize: 12, fontWeight: 800, background: 'var(--ok-soft)', color: 'var(--ok)' }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--ok)' }} />Aktiv
            </span>
          </div>
          <div className="flex flex-col" style={{ gap: 2 }}>
            <h2 className="m-0" style={{ fontSize: 17, fontWeight: 800, letterSpacing: '-0.02em' }}>Login-Schutz</h2>
            <span style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--ok)' }}>2FA aktiviert</span>
          </div>
        </section>
        <button type="button" onClick={onHistorie} data-tip="Login-Historie – Die letzten 20 Anmeldungen mit Gerät und Zeitpunkt"
          className="immo-glas flex flex-col justify-between text-left transition-transform duration-300 hover:-translate-y-[3px]"
          style={{ ...karte, border: 'none', cursor: 'pointer', fontFamily: 'inherit', color: 'var(--ink)' }}>
          <span className="flex w-full items-start justify-between" style={{ gap: 10 }}>
            <span className="flex items-center justify-center" style={{ width: 46, height: 46, borderRadius: 14, background: verlauf('--warn'), color: '#fff',
              boxShadow: 'inset 0 1px 1px rgba(255,255,255,0.35), 0 12px 24px -12px var(--warn)' }}>
              <ClockCounterClockwise size={23} weight="fill" />
            </span>
            <span className="flex items-center justify-center" style={{ width: 30, height: 30, borderRadius: '50%', background: 'var(--fill2)', color: 'var(--ink2)' }}>
              <ArrowUpRight size={14} weight="bold" />
            </span>
          </span>
          <span className="flex flex-col" style={{ gap: 2 }}>
            <span style={{ fontSize: 17, fontWeight: 800, letterSpacing: '-0.02em' }}>Login-Historie</span>
            <span style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--ink3)' }}>Einsehen</span>
          </span>
        </button>
      </div>
    </div>
  );
}
